import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { resolveOfferCodeType, offerCodeTypeLabel } from '../constants/offerCodeTypes';

function schoolDisplayName(school) {
  return school?.officialName || school?.answers?.officialName || 'School';
}

function defaultPaidDate() {
  return new Date().toISOString().slice(0, 10);
}

function defaultAmountGbp(school) {
  const fee = school?.appliedOffer?.feeGbp;
  if (fee != null && Number.isFinite(Number(fee))) return String(Math.round(Number(fee)));
  return '';
}

const backdropStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(2, 6, 23, 0.72)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 2100,
  padding: '20px',
};

const panelStyle = {
  width: 'min(480px, 96vw)',
  maxHeight: '90vh',
  overflow: 'auto',
  background: '#0f172a',
  border: '1px solid rgba(217, 119, 6, 0.4)',
  borderRadius: '10px',
  boxShadow: '0 24px 64px rgba(2, 6, 23, 0.6)',
  color: '#e2e8f0',
  padding: '20px',
};

/**
 * In-app confirm form for partner / external registration payments (O3b).
 * Portaled to document.body so SchoolRow overflow:hidden cannot clip it.
 */
const ExternalPaymentConfirmModal = ({
  school,
  isOpen,
  isSubmitting = false,
  onClose,
  onConfirm,
}) => {
  const [amountGbp, setAmountGbp] = useState('');
  const [paidAt, setPaidAt] = useState(defaultPaidDate());
  const [externalReference, setExternalReference] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!isOpen || !school) return;
    setAmountGbp(defaultAmountGbp(school));
    setPaidAt(defaultPaidDate());
    setExternalReference('');
    setNote('');
  }, [isOpen, school?.id]);

  if (!isOpen || !school || typeof document === 'undefined') return null;

  const codeType = resolveOfferCodeType(school.appliedOffer);
  const partnerLabel = school.appliedOffer?.label || school.appliedOffer?.partnerName || '';

  const handleSubmit = (e) => {
    e.preventDefault();
    onConfirm({
      amountGbp: amountGbp === '' ? null : Number(amountGbp),
      paidAt,
      externalReference: externalReference.trim(),
      note: note.trim(),
    });
  };

  return createPortal(
    <div
      className="modal-backdrop"
      role="presentation"
      style={backdropStyle}
      onClick={(e) => {
        if (e.target === e.currentTarget && !isSubmitting) onClose();
      }}
    >
      <div
        className="modal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="external-payment-confirm-title"
        style={panelStyle}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="external-payment-confirm-title" style={{ marginTop: 0, color: '#fff' }}>
          Confirm partner payment
        </h3>
        <p style={{ color: '#94a3b8', fontSize: '0.85rem', lineHeight: 1.5, marginTop: 0 }}>
          Mark external payment as received for{' '}
          <strong style={{ color: '#e2e8f0' }}>{schoolDisplayName(school)}</strong>. This sets{' '}
          <code style={{ color: '#cbd5e1' }}>paymentStatus</code> to paid and records finance ledger
          revenue when enabled.
        </p>

        <div
          style={{
            background: 'rgba(217, 119, 6, 0.12)',
            border: '1px solid rgba(217, 119, 6, 0.35)',
            borderRadius: '8px',
            padding: '12px 14px',
            marginBottom: '20px',
            fontSize: '0.8rem',
            color: '#fde68a',
            lineHeight: 1.45,
          }}
        >
          Offer <strong>{school.appliedOffer?.code || '—'}</strong> ({offerCodeTypeLabel(codeType)})
          {partnerLabel ? <> · {partnerLabel}</> : null}
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gap: '14px', marginBottom: '20px' }}>
            <label
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '6px',
                fontSize: '0.8rem',
                color: '#94a3b8',
              }}
            >
              Amount received (GBP)
              <input
                type="number"
                min="0"
                step="1"
                required
                value={amountGbp}
                onChange={(e) => setAmountGbp(e.target.value)}
                style={{
                  background: '#0f172a',
                  border: '1px solid #334155',
                  color: 'white',
                  padding: '8px',
                  borderRadius: '4px',
                }}
              />
            </label>
            <label
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '6px',
                fontSize: '0.8rem',
                color: '#94a3b8',
              }}
            >
              Payment date
              <input
                type="date"
                required
                value={paidAt}
                onChange={(e) => setPaidAt(e.target.value)}
                style={{
                  background: '#0f172a',
                  border: '1px solid #334155',
                  color: 'white',
                  padding: '8px',
                  borderRadius: '4px',
                }}
              />
            </label>
            <label
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '6px',
                fontSize: '0.8rem',
                color: '#94a3b8',
              }}
            >
              Bank / partner reference (optional)
              <input
                type="text"
                maxLength={200}
                value={externalReference}
                onChange={(e) => setExternalReference(e.target.value)}
                placeholder="e.g. invoice number, transfer ref"
                style={{
                  background: '#0f172a',
                  border: '1px solid #334155',
                  color: 'white',
                  padding: '8px',
                  borderRadius: '4px',
                }}
              />
            </label>
            <label
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '6px',
                fontSize: '0.8rem',
                color: '#94a3b8',
              }}
            >
              Internal note (optional)
              <textarea
                rows={2}
                maxLength={500}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Visible in system logs only"
                style={{
                  background: '#0f172a',
                  border: '1px solid #334155',
                  color: 'white',
                  padding: '8px',
                  borderRadius: '4px',
                  resize: 'vertical',
                }}
              />
            </label>
          </div>

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button type="button" className="btn-secondary" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </button>
            <button type="submit" className="btn-lifecycle-green" disabled={isSubmitting}>
              {isSubmitting ? 'Confirming…' : 'Confirm payment received'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default ExternalPaymentConfirmModal;
